import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import {
  HardDrive, Folder, File, Image, FileText, FileVideo, FileAudio, Search,
  Upload, Plus, Download, Link2, Trash2, Star, ChevronRight, Grid3x3,
  List, Loader2, X, Edit2, Check, Clock, Users, RefreshCw,
  MoreVertical, Building2, ArrowUpDown, ChevronDown, Eye,
} from 'lucide-react';
import GoogleConnectPrompt from '../components/GoogleConnectPrompt';

const VIEWS = [
  { id: 'shared-drives', label: '공유 드라이브', icon: Building2 },
  { id: 'root', label: '내 드라이브', icon: HardDrive },
  { id: 'recent', label: '최근 파일', icon: Clock },
  { id: 'starred', label: '별표', icon: Star },
  { id: 'shared', label: '공유됨', icon: Users },
];

const SORT_OPTIONS = [
  { id: 'name-asc',          label: '이름 (오름차순)' },
  { id: 'name-desc',         label: '이름 (내림차순)' },
  { id: 'modifiedTime-desc', label: '최근 수정순' },
  { id: 'modifiedTime-asc',  label: '오래된 순' },
  { id: 'size-desc',         label: '크기 큰 순' },
  { id: 'size-asc',          label: '크기 작은 순' },
];

const MIME_ICONS = {
  'application/vnd.google-apps.folder': Folder,
  'image/': Image,
  'video/': FileVideo,
  'audio/': FileAudio,
  'text/': FileText,
  'application/pdf': FileText,
};

const GDOC_EXT = {
  'application/vnd.google-apps.document':     'GDOC',
  'application/vnd.google-apps.spreadsheet':  'SHEET',
  'application/vnd.google-apps.presentation': 'SLIDE',
  'application/vnd.google-apps.form':         'FORM',
};

function getIcon(mimeType) {
  if (!mimeType) return File;
  for (const [k, v] of Object.entries(MIME_ICONS)) {
    if (mimeType === k || mimeType.startsWith(k)) return v;
  }
  return File;
}

function getExt(file) {
  if (GDOC_EXT[file.mimeType]) return GDOC_EXT[file.mimeType];
  const m = file.name?.match(/\.([^.]+)$/);
  return m ? m[1].toUpperCase() : '';
}

function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
}

function fmtDate(str) {
  if (!str) return '';
  return new Date(str).toLocaleDateString('ko-KR', { year: '2-digit', month: 'short', day: 'numeric' });
}

function isFolder(f) { return f.mimeType === 'application/vnd.google-apps.folder'; }
function isImage(f)  { return f.mimeType?.startsWith('image/'); }
function isGoogleDoc(f) { return f.mimeType?.startsWith('application/vnd.google-apps.') && !isFolder(f); }

// 썸네일 URL 크게 확대 (Google thumbnail URL의 =s220 → =s1200)
function bigThumb(url) {
  if (!url) return url;
  return url.replace(/=s\d+$/, '=s1200').replace(/=w\d+-h\d+/, '=w1200-h900');
}

// 확장자별 뱃지 색상
const EXT_COLORS = {
  PDF: 'bg-red-600', GDOC: 'bg-blue-600', SHEET: 'bg-green-600', SLIDE: 'bg-orange-500', FORM: 'bg-purple-500',
  JPG: 'bg-sky-600', JPEG: 'bg-sky-600', PNG: 'bg-sky-600', GIF: 'bg-sky-600', WEBP: 'bg-sky-600',
  MP4: 'bg-violet-600', MOV: 'bg-violet-600', AVI: 'bg-violet-600',
  ZIP: 'bg-gray-500', RAR: 'bg-gray-500',
  XLS: 'bg-green-700', XLSX: 'bg-green-700', DOC: 'bg-blue-700', DOCX: 'bg-blue-700',
  PPT: 'bg-orange-600', PPTX: 'bg-orange-600',
};

function ExtBadge({ file }) {
  const ext = getExt(file);
  if (!ext || isFolder(file)) return null;
  const color = EXT_COLORS[ext] || 'bg-gray-600';
  return (
    <span className={`absolute bottom-1.5 right-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded ${color} leading-none`} style={{ color: '#fff' }}>
      {ext}
    </span>
  );
}

export default function DrivePage() {
  const [connected, setConnected]     = useState(null);
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [viewMode, setViewMode]       = useState('shared-drives');
  const [gridView, setGridView]       = useState(true);
  const [sortKey, setSortKey]         = useState('name-asc');
  const [sortOpen, setSortOpen]       = useState(false);
  const [files, setFiles]             = useState([]);
  const [sharedDrives, setSharedDrives] = useState([]);
  const [activeDriveId, setActiveDriveId] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [refreshing, setRefreshing]   = useState(false);
  const [folderStack, setFolderStack] = useState([]);
  const [searchQ, setSearchQ]         = useState('');
  const [searching, setSearching]     = useState(false);
  const [selected, setSelected]       = useState(null);
  const [renaming, setRenaming]       = useState(null);
  const [renameVal, setRenameVal]     = useState('');
  const [uploading, setUploading]     = useState(false);
  const [newFolderMode, setNewFolderMode] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [contextMenu, setContextMenu] = useState(null);
  const [preview, setPreview]         = useState(null); // 미리보기 파일
  const fileInputRef = useRef();
  const navigate = useNavigate();
  const renameRef = useRef();

  useEffect(() => {
    api.get('/google/status').then(r => {
      setConnected(r.data.connected);
      setNeedsReconnect(r.data.needsReconnect || false);
      if (r.data.connected) loadFiles();
    }).catch(() => setConnected(false));
  }, []);

  const currentFolderId = folderStack.length > 0 ? folderStack[folderStack.length - 1].id : 'root';

  const loadFiles = useCallback(async (folderId = currentFolderId, view = viewMode, driveId = activeDriveId) => {
    setLoading(true);
    try {
      let r;
      if (view === 'shared-drives' && !driveId) {
        r = await api.get('/google/drive/drives');
        const drives = r.data.drives || [];
        setSharedDrives(drives);
        if (drives.length > 0) {
          const first = drives[0];
          setActiveDriveId(first.id);
          setFolderStack([{ id: first.id, name: first.name }]);
          const r2 = await api.get(`/google/drive/drives/${first.id}/files`);
          setFiles(r2.data.files || []);
        } else { setFiles([]); }
        setLoading(false); setRefreshing(false); return;
      } else if (view === 'shared-drives' && driveId) {
        r = await api.get(`/google/drive/drives/${driveId}/files`, { params: { folderId: folderId !== 'root' ? folderId : undefined } });
        setFiles(r.data.files || []);
      } else if (view === 'recent')  r = await api.get('/google/drive/recent');
      else if (view === 'starred')   r = await api.get('/google/drive/starred');
      else if (view === 'shared')    r = await api.get('/google/drive/shared');
      else r = await api.get('/google/drive/files', { params: { folderId } });
      if (view !== 'shared-drives') setFiles(r.data.files || []);
    } catch (e) {
      if (e.response?.status === 401) setConnected(false);
    } finally { setLoading(false); setRefreshing(false); }
  }, [currentFolderId, viewMode, activeDriveId]);

  useEffect(() => { if (connected) loadFiles(currentFolderId, viewMode); }, [viewMode, connected]);

  // 정렬된 파일 목록
  const sortedFiles = useMemo(() => {
    const [field, order] = sortKey.split('-');
    return [...files].sort((a, b) => {
      let va, vb;
      if (field === 'name') { va = a.name?.toLowerCase() || ''; vb = b.name?.toLowerCase() || ''; }
      else if (field === 'modifiedTime') { va = a.modifiedTime || ''; vb = b.modifiedTime || ''; }
      else if (field === 'size') { va = parseInt(a.size) || 0; vb = parseInt(b.size) || 0; }
      // 폴더 항상 앞으로
      if (isFolder(a) && !isFolder(b)) return -1;
      if (!isFolder(a) && isFolder(b)) return 1;
      if (va < vb) return order === 'asc' ? -1 : 1;
      if (va > vb) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [files, sortKey]);

  async function handleSearch(e) {
    e.preventDefault();
    if (!searchQ.trim()) { loadFiles(); return; }
    setSearching(true);
    try {
      const r = await api.get('/google/drive/search', { params: { q: searchQ } });
      setFiles(r.data.files || []); setFolderStack([]);
    } finally { setSearching(false); }
  }

  function openSharedDrive(drv) {
    setActiveDriveId(drv.id);
    setFolderStack([{ id: drv.id, name: drv.name }]);
    loadFiles('root', 'shared-drives', drv.id);
  }

  function openFolder(file) {
    if (viewMode === 'shared-drives' && activeDriveId) {
      setFolderStack(prev => [...prev, { id: file.id, name: file.name }]);
      loadFiles(file.id, 'shared-drives', activeDriveId);
    } else {
      setFolderStack(prev => [...prev, { id: file.id, name: file.name }]);
      loadFiles(file.id, 'root'); setViewMode('root');
    }
  }

  function goToFolder(idx) {
    const next = folderStack.slice(0, idx + 1);
    setFolderStack(next);
    if (viewMode === 'shared-drives') {
      if (idx === 0) loadFiles('root', 'shared-drives', activeDriveId);
      else loadFiles(next[next.length - 1].id, 'shared-drives', activeDriveId);
    } else loadFiles(next.length > 0 ? next[next.length - 1].id : 'root', 'root');
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    if (currentFolderId !== 'root') fd.append('folderId', currentFolderId);
    try {
      await api.post('/google/drive/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await loadFiles();
    } catch (e) { alert('업로드 실패: ' + e.message); }
    finally { setUploading(false); e.target.value = ''; }
  }

  async function handleNewFolder() {
    if (!newFolderName.trim()) return;
    await api.post('/google/drive/folders', { name: newFolderName.trim(), parentId: currentFolderId !== 'root' ? currentFolderId : undefined });
    setNewFolderMode(false); setNewFolderName(''); await loadFiles();
  }

  async function handleStar(file) {
    await api.patch(`/google/drive/files/${file.id}`, { starred: !file.starred });
    setFiles(prev => prev.map(f => f.id === file.id ? { ...f, starred: !f.starred } : f));
  }

  async function handleDelete(file) {
    if (!confirm(`'${file.name}'을(를) 휴지통으로 이동하시겠습니까?`)) return;
    await api.delete(`/google/drive/files/${file.id}`);
    setFiles(prev => prev.filter(f => f.id !== file.id));
    if (selected?.id === file.id) setSelected(null);
  }

  async function handleRename(fileId) {
    if (!renameVal.trim()) return;
    await api.patch(`/google/drive/files/${fileId}`, { name: renameVal.trim() });
    setFiles(prev => prev.map(f => f.id === fileId ? { ...f, name: renameVal.trim() } : f));
    setRenaming(null);
  }

  async function handleShare(file) {
    try {
      const r = await api.post(`/google/drive/files/${file.id}/share`, { role: 'reader' });
      await navigator.clipboard.writeText(r.data.link);
      alert('공유 링크가 클립보드에 복사되었습니다.');
    } catch (e) { alert('공유 링크 생성 실패: ' + e.message); }
  }

  async function handleDownload(file) {
    if (isGoogleDoc(file)) { window.open(file.webViewLink, '_blank'); return; }
    try {
      const r = await api.get(`/google/drive/files/${file.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert('다운로드 실패: ' + e.message); }
  }

  function handleContextMenu(e, file) {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, file });
  }

  function openPreview(file, e) {
    e?.stopPropagation();
    if (isFolder(file)) { openFolder(file); return; }
    setPreview(file);
  }

  useEffect(() => {
    if (contextMenu) {
      const close = () => setContextMenu(null);
      window.addEventListener('click', close);
      return () => window.removeEventListener('click', close);
    }
  }, [contextMenu]);

  // ESC로 미리보기/정렬 닫기
  useEffect(() => {
    const handler = e => {
      if (e.key === 'Escape') { setPreview(null); setSortOpen(false); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  if (connected === false) return <GoogleConnectPrompt height="h-96" reconnect={needsReconnect} />;

  const FileIcon = ({ file, size = 32 }) => {
    const I = getIcon(file.mimeType);
    const color = isFolder(file) ? '#fbbf24' : isImage(file) ? '#60a5fa' : isGoogleDoc(file) ? '#4ade80' : '#9ca3af';
    return <I size={size} style={{ color }} />;
  };

  // 균일한 카드 썸네일 영역
  function CardThumb({ file }) {
    return (
      <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden bg-gray-800 flex items-center justify-center">
        {isImage(file) && file.thumbnailLink ? (
          <img src={file.thumbnailLink} alt={file.name} className="w-full h-full object-cover" />
        ) : (
          <FileIcon file={file} size={36} />
        )}
        <ExtBadge file={file} />
        {/* 호버 시 미리보기 버튼 */}
        {!isFolder(file) && (
          <button
            onClick={e => openPreview(file, e)}
            className="absolute inset-0 bg-black/0 hover:bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-all"
          >
            <Eye size={20} className="text-white" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden" style={{ width: '100%' }}>
      {/* 사이드바 */}
      <div className="w-44 flex-shrink-0 border-r border-gray-800 flex flex-col bg-gray-900/50">
        <div className="p-3 space-y-1.5">
          <button onClick={() => fileInputRef.current?.click()} disabled={uploading} style={{ color: '#ffffff' }} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#e0005e] hover:bg-[#c0004e] text-sm font-semibold transition-colors disabled:opacity-60">
            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            {uploading ? '업로드 중...' : '파일 업로드'}
          </button>
          <input type="file" ref={fileInputRef} className="hidden" onChange={handleUpload} />
          <button onClick={() => { setNewFolderMode(true); setNewFolderName('새 폴더'); }} className="w-full flex items-center justify-center gap-2 py-2 rounded-xl border border-gray-700 hover:bg-gray-800 text-gray-300 text-sm font-medium transition-colors">
            <Plus size={13} /> 새 폴더
          </button>
        </div>
        <nav className="flex-1 px-2 space-y-0.5">
          {VIEWS.map(({ id, label, icon: I }) => (
            <button key={id}
              onClick={() => {
                setViewMode(id); setFolderStack([]); setSearchQ(''); setSelected(null);
                if (id === 'shared-drives') { if (viewMode === 'shared-drives' && connected) loadFiles('root', 'shared-drives', null); }
                else setActiveDriveId(null);
              }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                viewMode === id && !searchQ ? 'bg-[#e0005e]/15 text-[#e0005e] font-semibold' : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
              }`}
            >
              <I size={14} /> {label}
            </button>
          ))}
        </nav>
      </div>

      {/* 메인 */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* 툴바 */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-800">
          <div className="flex items-center gap-1 text-sm flex-1 min-w-0">
            {viewMode !== 'shared-drives' && (
              <button onClick={() => { setFolderStack([]); setViewMode('root'); setSearchQ(''); setActiveDriveId(null); loadFiles('root', 'root'); }} className="text-gray-400 hover:text-gray-200">내 드라이브</button>
            )}
            {folderStack.map((f, i) => (
              <span key={f.id} className="flex items-center gap-1">
                {(viewMode !== 'shared-drives' || i > 0) && <ChevronRight size={12} className="text-gray-600" />}
                <button onClick={() => goToFolder(i)} className={`truncate max-w-[100px] ${i < folderStack.length - 1 ? 'text-gray-400 hover:text-gray-200' : 'text-gray-200 cursor-default'}`}>
                  {f.name}
                </button>
              </span>
            ))}
            {searchQ && <span className="text-gray-500 ml-1">— "{searchQ}" 검색 결과</span>}
          </div>

          <form onSubmit={handleSearch} className="flex gap-1.5">
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
              <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="파일 검색..."
                className="pl-7 pr-3 py-1.5 rounded-lg bg-gray-800 border border-gray-700 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-500 w-44"
              />
            </div>
            {searchQ && <button type="button" onClick={() => { setSearchQ(''); loadFiles(); }} className="p-1.5 rounded hover:bg-gray-800 text-gray-500"><X size={13} /></button>}
          </form>

          {/* 정렬 드롭다운 */}
          <div className="relative">
            <button
              onClick={e => { e.stopPropagation(); setSortOpen(v => !v); }}
              className="flex items-center gap-1 p-1.5 rounded hover:bg-gray-800 text-gray-500 hover:text-gray-300 text-xs"
              title="정렬"
            >
              <ArrowUpDown size={13} />
              <ChevronDown size={10} />
            </button>
            {sortOpen && (
              <div className="absolute right-0 top-full mt-1 z-50 bg-gray-900 border border-gray-700 rounded-xl py-1 min-w-[160px] text-xs" onClick={e => e.stopPropagation()}>
                {SORT_OPTIONS.map(opt => (
                  <button key={opt.id} onClick={() => { setSortKey(opt.id); setSortOpen(false); }}
                    className={`w-full text-left px-3 py-2 hover:bg-gray-800 transition-colors ${sortKey === opt.id ? 'text-[#e0005e] font-semibold' : 'text-gray-300'}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button onClick={() => { setRefreshing(true); loadFiles(); }} disabled={refreshing} className="p-1.5 rounded hover:bg-gray-800 text-gray-500 hover:text-gray-300">
            <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
          </button>
          <button onClick={() => setGridView(v => !v)} className="p-1.5 rounded hover:bg-gray-800 text-gray-500 hover:text-gray-300">
            {gridView ? <List size={13} /> : <Grid3x3 size={13} />}
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4" onClick={() => { setSelected(null); setContextMenu(null); setSortOpen(false); }}>
            {newFolderMode && (
              <div className="mb-3 flex items-center gap-2">
                <Folder size={18} className="text-yellow-400" />
                <input autoFocus value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleNewFolder(); if (e.key === 'Escape') setNewFolderMode(false); }}
                  className="px-2 py-1 rounded bg-gray-800 border border-gray-600 text-sm text-gray-200 focus:outline-none"
                />
                <button onClick={handleNewFolder} className="p-1 rounded bg-green-600 hover:bg-green-500 text-white"><Check size={13} /></button>
                <button onClick={() => setNewFolderMode(false)} className="p-1 rounded hover:bg-gray-700 text-gray-400"><X size={13} /></button>
              </div>
            )}

            {/* 공유 드라이브 목록 */}
            {viewMode === 'shared-drives' && !activeDriveId && !loading && (
              sharedDrives.length === 0 ? (
                <div className="text-center py-20 text-gray-500 text-sm">공유 드라이브가 없습니다.</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 p-4">
                  {sharedDrives.map(drv => (
                    <div key={drv.id} onClick={() => openSharedDrive(drv)}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl border border-gray-700 hover:border-[#e0005e]/50 hover:bg-[#e0005e]/5 cursor-pointer transition-all">
                      <Building2 size={36} className="text-[#e0005e]" />
                      <span className="text-sm text-gray-200 text-center font-medium truncate w-full">{drv.name}</span>
                    </div>
                  ))}
                </div>
              )
            )}

            {loading ? (
              <div className="flex items-center justify-center h-48"><Loader2 className="animate-spin text-gray-500" size={24} /></div>
            ) : (viewMode !== 'shared-drives' || activeDriveId) && sortedFiles.length === 0 ? (
              <div className="text-center py-20 text-gray-500 text-sm">파일이 없습니다.</div>
            ) : (viewMode !== 'shared-drives' || activeDriveId) && gridView ? (
              /* 그리드 뷰 — 균일한 카드 */
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                {sortedFiles.map(file => (
                  <div key={file.id}
                    onClick={e => { e.stopPropagation(); setSelected(file); }}
                    onDoubleClick={() => isFolder(file) ? openFolder(file) : openPreview(file)}
                    onContextMenu={e => handleContextMenu(e, file)}
                    className={`group rounded-xl border cursor-pointer transition-all ${
                      selected?.id === file.id ? 'border-[#e0005e]/50 bg-[#e0005e]/5' : 'border-gray-800 hover:border-gray-700 hover:bg-gray-800/30'
                    }`}
                  >
                    <div className="p-2">
                      <CardThumb file={file} />
                      {renaming === file.id ? (
                        <input ref={renameRef} autoFocus value={renameVal} onChange={e => setRenameVal(e.target.value)}
                          onBlur={() => handleRename(file.id)}
                          onKeyDown={e => { if (e.key === 'Enter') handleRename(file.id); if (e.key === 'Escape') setRenaming(null); }}
                          onClick={e => e.stopPropagation()}
                          className="mt-1.5 w-full text-xs text-center bg-gray-700 rounded px-1 py-0.5 focus:outline-none text-gray-200"
                        />
                      ) : (
                        <p className="mt-1.5 text-xs text-gray-300 text-center leading-tight line-clamp-2 w-full px-0.5">{file.name}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (viewMode !== 'shared-drives' || activeDriveId) && (
              /* 리스트 뷰 */
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-gray-500 border-b border-gray-800">
                  <th className="text-left pb-2 font-medium">이름</th>
                  <th className="text-left pb-2 font-medium hidden sm:table-cell">수정일</th>
                  <th className="text-left pb-2 font-medium hidden md:table-cell">크기</th>
                  <th className="pb-2 w-8"></th>
                </tr></thead>
                <tbody>
                  {sortedFiles.map(file => (
                    <tr key={file.id}
                      onClick={e => { e.stopPropagation(); setSelected(file); }}
                      onDoubleClick={() => isFolder(file) ? openFolder(file) : openPreview(file)}
                      onContextMenu={e => handleContextMenu(e, file)}
                      className={`border-b border-gray-800/40 cursor-pointer transition-colors group ${selected?.id === file.id ? 'bg-[#e0005e]/5' : 'hover:bg-gray-800/30'}`}
                    >
                      <td className="py-2">
                        <div className="flex items-center gap-2">
                          <div className="relative flex-shrink-0">
                            <FileIcon file={file} size={16} />
                          </div>
                          {renaming === file.id ? (
                            <input autoFocus ref={renameRef} value={renameVal} onChange={e => setRenameVal(e.target.value)}
                              onBlur={() => handleRename(file.id)}
                              onKeyDown={e => { if (e.key === 'Enter') handleRename(file.id); if (e.key === 'Escape') setRenaming(null); }}
                              onClick={e => e.stopPropagation()}
                              className="flex-1 bg-gray-700 rounded px-1 py-0.5 text-sm focus:outline-none text-gray-200" />
                          ) : (
                            <span className="text-gray-200 truncate">{file.name}</span>
                          )}
                          {getExt(file) && !isFolder(file) && (
                            <span className={`flex-shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded leading-none ${EXT_COLORS[getExt(file)] || 'bg-gray-600'}`} style={{ color: '#fff' }}>
                              {getExt(file)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 text-gray-500 text-xs hidden sm:table-cell">{fmtDate(file.modifiedTime)}</td>
                      <td className="py-2 text-gray-500 text-xs hidden md:table-cell">{isFolder(file) ? '—' : fmtSize(parseInt(file.size))}</td>
                      <td className="py-2">
                        <button onClick={e => { e.stopPropagation(); setContextMenu({ x: e.clientX, y: e.clientY, file }); }}
                          className="p-1 rounded hover:bg-gray-700 text-gray-500 opacity-0 group-hover:opacity-100">
                          <MoreVertical size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* 상세 패널 */}
          {selected && (
            <div className="w-56 flex-shrink-0 border-l border-gray-800 p-4 space-y-4">
              <div className="flex flex-col items-center gap-2 w-full">
                {isImage(selected) && selected.thumbnailLink ? (
                  <img src={selected.thumbnailLink} alt={selected.name} className="w-full aspect-[4/3] object-cover rounded-lg" />
                ) : (
                  <div className="w-full aspect-[4/3] flex items-center justify-center bg-gray-800 rounded-lg">
                    <FileIcon file={selected} size={40} />
                  </div>
                )}
                <p className="text-sm font-medium text-gray-200 text-center break-all">{selected.name}</p>
              </div>
              <div className="text-xs text-gray-500 space-y-1.5">
                <div><span className="text-gray-400">유형</span><p>{getExt(selected) || selected.mimeType?.split('/')[1] || '—'}</p></div>
                {selected.size && <div><span className="text-gray-400">크기</span><p>{fmtSize(parseInt(selected.size))}</p></div>}
                <div><span className="text-gray-400">수정일</span><p>{fmtDate(selected.modifiedTime)}</p></div>
              </div>
              <div className="space-y-1.5">
                {isFolder(selected) ? (
                  <button onClick={() => openFolder(selected)} className="w-full py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-sm text-gray-200">열기</button>
                ) : (
                  <>
                    <button onClick={() => openPreview(selected)} className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-sm text-gray-200">
                      <Eye size={13} /> 미리보기
                    </button>
                    <button onClick={() => handleDownload(selected)} className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-sm text-gray-200">
                      <Download size={13} /> {isGoogleDoc(selected) ? '열기' : '다운로드'}
                    </button>
                  </>
                )}
                <button onClick={() => handleShare(selected)} className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-sm text-gray-200">
                  <Link2 size={13} /> 공유 링크 복사
                </button>
                <button onClick={() => { setRenaming(selected.id); setRenameVal(selected.name); }} className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-sm text-gray-200">
                  <Edit2 size={13} /> 이름 바꾸기
                </button>
                <button onClick={() => handleStar(selected)} className={`w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-sm transition-colors ${selected.starred ? 'text-yellow-400' : 'text-gray-200'}`}>
                  <Star size={13} fill={selected.starred ? 'currentColor' : 'none'} /> {selected.starred ? '별표 제거' : '별표'}
                </button>
                <button onClick={() => handleDelete(selected)} className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg hover:bg-red-900/30 text-sm text-gray-500 hover:text-red-400">
                  <Trash2 size={13} /> 삭제
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 우클릭 컨텍스트 메뉴 */}
      {contextMenu && (
        <div className="fixed z-50 bg-gray-900 border border-gray-700 rounded-xl shadow-xl py-1 min-w-[160px] text-sm"
          style={{ top: contextMenu.y, left: contextMenu.x }} onClick={e => e.stopPropagation()}>
          {isFolder(contextMenu.file) ? (
            <button onClick={() => { openFolder(contextMenu.file); setContextMenu(null); }} className="w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-800 text-gray-200"><Folder size={13} /> 열기</button>
          ) : (
            <>
              <button onClick={() => { openPreview(contextMenu.file); setContextMenu(null); }} className="w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-800 text-gray-200"><Eye size={13} /> 미리보기</button>
              <button onClick={() => { handleDownload(contextMenu.file); setContextMenu(null); }} className="w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-800 text-gray-200"><Download size={13} /> {isGoogleDoc(contextMenu.file) ? '열기' : '다운로드'}</button>
            </>
          )}
          <button onClick={() => { handleShare(contextMenu.file); setContextMenu(null); }} className="w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-800 text-gray-200"><Link2 size={13} /> 공유 링크 복사</button>
          <button onClick={() => { setRenaming(contextMenu.file.id); setRenameVal(contextMenu.file.name); setSelected(contextMenu.file); setContextMenu(null); }} className="w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-800 text-gray-200"><Edit2 size={13} /> 이름 바꾸기</button>
          <button onClick={() => { handleStar(contextMenu.file); setContextMenu(null); }} className={`w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-800 ${contextMenu.file.starred ? 'text-yellow-400' : 'text-gray-200'}`}>
            <Star size={13} fill={contextMenu.file.starred ? 'currentColor' : 'none'} /> {contextMenu.file.starred ? '별표 제거' : '별표'}
          </button>
          <div className="border-t border-gray-800 my-1" />
          <button onClick={() => { handleDelete(contextMenu.file); setContextMenu(null); }} className="w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-800 text-red-400"><Trash2 size={13} /> 휴지통으로 이동</button>
        </div>
      )}

      {/* 미리보기 모달 */}
      {preview && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="relative max-w-4xl w-full flex flex-col rounded-xl overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* 상단 바 */}
            <div className="flex items-center justify-between bg-gray-900 px-4 py-2.5 border-b border-gray-700">
              <div className="flex items-center gap-2 min-w-0">
                {getExt(preview) && (
                  <span className={`text-[10px] font-bold text-white px-2 py-0.5 rounded flex-shrink-0 ${EXT_COLORS[getExt(preview)] || 'bg-gray-600'}`}>
                    {getExt(preview)}
                  </span>
                )}
                <span className="text-gray-100 text-sm font-medium truncate">{preview.name}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                <button
                  onClick={() => handleDownload(preview)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-gray-800 text-xs font-medium hover:bg-gray-100 transition-colors"
                >
                  <Download size={12} /> {isGoogleDoc(preview) ? '열기' : '다운로드'}
                </button>
                <button
                  onClick={() => setPreview(null)}
                  className="p-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-white transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
            {/* 콘텐츠 */}
            {isImage(preview) && preview.thumbnailLink ? (
              <img
                src={bigThumb(preview.thumbnailLink)}
                alt={preview.name}
                className="max-h-[80vh] max-w-full object-contain bg-gray-950"
              />
            ) : (
              <div className="w-full h-[75vh] bg-gray-900">
                <iframe
                  src={`https://drive.google.com/file/d/${preview.id}/preview`}
                  className="w-full h-full border-0"
                  allow="autoplay"
                  title={preview.name}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
